/**
 * Configurator: flat Klaviyo/Zapier event fields + build summary HTML
 */

(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(amount) {
    if (amount === null || amount === undefined || isNaN(amount)) return '';
    return '$' + Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  function isUnavailablePlaceholder(text) {
    if (!text || !String(text).trim()) return true;
    var normalized = String(text).replace(/\s+/g, ' ').trim();
    return /variant sold out or unavailable|value_unavailable/i.test(normalized) && normalized.length < 120;
  }

  /* Pill labels render title + price with no space ("Lighting Kit$850.00"), so strip any $ amount */
  function stripPriceFromLabel(text) {
    if (!text) return '';
    return String(text)
      .replace(/\s*\+?\$[\d,]+(?:\.\d{2})?/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cleanOptionText(text) {
    var cleaned = stripPriceFromLabel(text);
    if (!cleaned || isUnavailablePlaceholder(cleaned)) return '';
    if (/^\+\$[\d,]+(?:\.\d{2})?$/.test(cleaned)) return '';
    return cleaned;
  }

  var CONFIG_OPTION_PRIORITY = ['canopy color', 'tent color', 'size'];

  function normalizeOptionLabel(entry, key) {
    return String((entry && entry.label) || (entry && entry.name) || key || '')
      .toLowerCase()
      .trim();
  }

  function configurationOptionSortRank(entry, key) {
    var label = normalizeOptionLabel(entry, key);
    if (label === 'canopy color' || label.indexOf('canopy') === 0) return 0;
    if (label === 'tent color' || label.indexOf('tent color') === 0) return 1;
    if (label === 'size') return 2;
    return CONFIG_OPTION_PRIORITY.length;
  }

  function sortConfigurationOptionKeys(keys, optionsByKey) {
    return keys.slice().sort(function (a, b) {
      var rankA = configurationOptionSortRank(optionsByKey[a], a);
      var rankB = configurationOptionSortRank(optionsByKey[b], b);
      if (rankA !== rankB) return rankA - rankB;
      return normalizeOptionLabel(optionsByKey[a], a).localeCompare(
        normalizeOptionLabel(optionsByKey[b], b)
      );
    });
  }

  /* Size ("Mid Size Truck") repeats the Your Truck row, so it is left out of the summary */
  function isSummaryOption(entry, key) {
    if (!entry || !entry.selected) return false;
    return normalizeOptionLabel(entry, key) !== 'size';
  }

  function optionDisplayValue(entry) {
    if (!entry) return '';
    if (entry.selections && entry.selections.length) {
      return entry.selections
        .filter(function (s) { return s.selected; })
        .map(function (s) {
          var fromLabel = cleanOptionText(s.label);
          if (fromLabel) return fromLabel;
          return cleanOptionText(s.value);
        })
        .filter(Boolean)
        .join(', ');
    }
    var fromSelection = cleanOptionText(entry.selectionLabel);
    if (fromSelection) return fromSelection;
    return cleanOptionText(entry.value);
  }

  /* Variant prices are parsed from the value text ("5' Bed" -> 5), so only trust add-on option prices */
  function optionPrice(entry) {
    if (!entry || entry.type === 'variant') return null;
    if (entry.selections && entry.selections.length) {
      return entry.selections.reduce(function (sum, s) {
        return s.selected && s.price ? sum + Number(s.price) : sum;
      }, 0) || null;
    }
    return entry.price ? Number(entry.price) : null;
  }

  function buildOptionsLines(optionsByKey) {
    var lines = [];
    sortConfigurationOptionKeys(Object.keys(optionsByKey || {}), optionsByKey).forEach(function (key) {
      var entry = optionsByKey[key];
      if (!isSummaryOption(entry, key)) return;
      var value = optionDisplayValue(entry);
      if (!value) return;
      lines.push((entry.label || key) + ': ' + value);
    });
    return lines;
  }

  function buildAddOnLines(addOnsByKey, selectedOnly) {
    var lines = [];
    Object.keys(addOnsByKey || {}).sort().forEach(function (key) {
      var addOn = addOnsByKey[key];
      if (!addOn) return;
      if (selectedOnly && !addOn.selected) return;
      if (!selectedOnly && addOn.selected) return;
      var price = addOn.price ? ' (+' + formatMoney(addOn.price) + ')' : '';
      lines.push(addOn.title + price);
    });
    return lines;
  }

  /* Match email-templates/klaviyo-configurator-build-email.html */
  var EMAIL = {
    font: 'Arial,Helvetica,sans-serif',
    text: '#35322e',
    muted: '#666666',
    mutedAlt: '#999999',
    panelBg: '#f8f8f8',
    barBg: '#35322e',
    barText: '#ffffff',
  };

  var CONFIGURATOR_DEPOSIT = 1000;

  function formatInstallLocationLabel(location) {
    if (!location) return '';
    if (typeof location === 'string') return String(location).replace(/\s+/g, ' ').trim();
    return String(
      location.locationName || location.label || location.value || ''
    ).replace(/\s+/g, ' ').trim();
  }

  function formatVehicleLabel(label) {
    if (!label) return '';
    var text = String(label).replace(/\s+/g, ' ').trim();
    var parts = text.split('|').map(function (part) { return part.trim(); }).filter(Boolean);
    return parts.length >= 2 ? parts[0] + ' | ' + parts[1] : text;
  }

  /* "Toyota Tacoma 5' Bed" + "Gen 4" -> "Toyota Tacoma 5' Bed (Gen 4)", unless the label already names it */
  function truckDisplayValue(vehicleLabel, generation) {
    if (!vehicleLabel) return '';
    var gen = String(generation || '').trim();
    if (!gen || vehicleLabel.toLowerCase().indexOf(gen.toLowerCase()) !== -1) return vehicleLabel;
    return vehicleLabel + ' (' + gen + ')';
  }

  function renderBuildSummaryHtml(build) {
    if (!build) return '';

    var parts = [];
    var optionsByKey = build.optionsByKey || {};
    var addOnsByKey = build.addOnsByKey || {};
    var pricing = build.pricing || {};
    var productTitle = (build.product && build.product.title) || '';

    var optionRows = [];
    var truck = truckDisplayValue(
      formatVehicleLabel(build.vehicle ? (build.vehicle.label || build.vehicle.value || '') : ''),
      build.generation
    );
    if (truck) {
      optionRows.push({ label: 'Your Truck', value: truck, valueBold: true });
    }
    var installLocationLabel = formatInstallLocationLabel(build.installLocation);
    if (installLocationLabel) {
      optionRows.push({ label: 'Install location', value: installLocationLabel });
    }
    sortConfigurationOptionKeys(Object.keys(optionsByKey), optionsByKey).forEach(function (key) {
      var entry = optionsByKey[key];
      if (!isSummaryOption(entry, key)) return;
      var value = optionDisplayValue(entry);
      if (!value) return;
      var price = optionPrice(entry);
      optionRows.push({
        label: entry.label || key,
        value: value,
        price: price ? '+' + formatMoney(price) : '',
      });
    });

    var selectedAddOns = buildAddOnLines(addOnsByKey, true);
    var declinedAddOns = buildAddOnLines(addOnsByKey, false);

    var pricingRows = [];
    if (pricing && (pricing.base !== null || pricing.total !== null)) {
      if (pricing.base !== null) {
        pricingRows.push({ label: 'Base', value: formatMoney(pricing.base) });
      }
      if (pricing.addOnsTotal !== null) {
        pricingRows.push({ label: 'Options', value: formatMoney(pricing.addOnsTotal) });
      }
      if (pricing.requiredAddonsTotal) {
        pricingRows.push({ label: 'Required addons', value: formatMoney(pricing.requiredAddonsTotal) });
      }
      if (pricing.installFeeWaived) {
        pricingRows.push({ label: 'Install fee', value: 'Waived' });
      } else if (pricing.installFee !== null) {
        pricingRows.push({ label: 'Install fee', value: formatMoney(pricing.installFee) });
      }
      if (pricing.shipping !== null && pricing.shipping !== undefined) {
        pricingRows.push({ label: 'Shipping', value: formatMoney(pricing.shipping) });
      }
      if (pricing.total !== null) {
        pricingRows.push({
          label: 'Estimated total',
          value: formatMoney(pricing.total),
          valueBold: true,
          valueSize: '18px',
        });
      }
      pricingRows.push({
        label: 'Deposit (Due at time of purchase)',
        value: formatMoney(CONFIGURATOR_DEPOSIT),
      });
    }

    if (optionRows.length) {
      parts.push(sectionBar(productTitle ? 'Your ' + productTitle + ' configuration' : 'Your configuration'));
      parts.push(keyValuePanel(optionRows));
    }

    if (selectedAddOns.length) {
      parts.push(sectionBar('Selected add-ons'));
      parts.push(listPanel(selectedAddOns, EMAIL.text));
    }

    if (declinedAddOns.length) {
      /* Kept as a light label, not a dark bar, so it reads as secondary */
      parts.push(mutedHeading('Available add-ons not selected'));
      parts.push(listPanel(declinedAddOns, EMAIL.muted, true));
    }

    if (pricingRows.length) {
      parts.push(sectionBar('Pricing as configured'));
      parts.push(keyValuePanel(pricingRows));
    }

    return parts.join('');

    function sectionBar(title) {
      return [
        '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">',
        '<tr><td style="background-color:', EMAIL.barBg, ';padding:18px 24px;font-family:', EMAIL.font, ';',
        'font-size:13px;font-weight:700;line-height:1.3;letter-spacing:0.08em;text-transform:uppercase;color:', EMAIL.barText, ';">',
        escapeHtml(title),
        '</td></tr></table>',
      ].join('');
    }

    function mutedHeading(title) {
      return [
        '<p style="margin:0 0 8px;font-family:', EMAIL.font, ';font-size:12px;font-weight:700;',
        'letter-spacing:0.08em;text-transform:uppercase;color:', EMAIL.mutedAlt, ';">',
        escapeHtml(title),
        '</p>',
      ].join('');
    }

    /* Panels sit directly under a dark bar, so only the bottom corners are rounded */
    function panelTableOpen(standalone) {
      return [
        '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="',
        'background-color:', EMAIL.panelBg, ';',
        'border-radius:', standalone ? '6px' : '0 0 6px 6px', ';border-collapse:separate;',
        'font-family:', EMAIL.font, ';font-size:15px;line-height:1.5;margin:0 0 24px;">',
      ].join('');
    }

    function panelTableClose() {
      return '</table>';
    }

    function rowPadding(index, total) {
      if (total === 1) return '20px 24px';
      if (index === 0) return '20px 24px 8px';
      if (index === total - 1) return '8px 24px 20px';
      return '8px 24px';
    }

    function keyValuePanel(rows) {
      var html = [panelTableOpen(false)];
      rows.forEach(function (item, index) {
        var pad = rowPadding(index, rows.length);
        var valueStyle = [
          'padding:', pad, ';color:', EMAIL.text, ';',
          item.valueBold ? 'font-weight:700;' : '',
          item.valueSize ? 'font-size:' + item.valueSize + ';' : '',
        ].join('');
        html.push(
          '<tr>',
          '<td style="padding:', pad, ';color:', EMAIL.muted, ';width:42%;vertical-align:top;">',
          escapeHtml(item.label),
          '</td>',
          '<td style="', valueStyle, 'vertical-align:top;">',
          escapeHtml(item.value),
          item.price
            ? '<span style="color:' + EMAIL.muted + ';white-space:nowrap;">&nbsp;&nbsp;' + escapeHtml(item.price) + '</span>'
            : '',
          '</td>',
          '</tr>'
        );
      });
      html.push(panelTableClose());
      return html.join('');
    }

    function listPanel(lines, color, standalone) {
      var html = [panelTableOpen(standalone)];
      lines.forEach(function (line, index) {
        html.push(
          '<tr><td colspan="2" style="padding:',
          rowPadding(index, lines.length),
          ';color:', color, ';">',
          escapeHtml(line),
          '</td></tr>'
        );
      });
      html.push(panelTableClose());
      return html.join('');
    }
  }

  function buildOptionsPricedLines(optionsByKey) {
    var lines = [];
    sortConfigurationOptionKeys(Object.keys(optionsByKey || {}), optionsByKey).forEach(function (key) {
      var entry = optionsByKey[key];
      if (!isSummaryOption(entry, key)) return;
      var value = optionDisplayValue(entry);
      if (!value) return;
      var price = optionPrice(entry);
      lines.push((entry.label || key) + ': ' + value + (price ? ' (+' + formatMoney(price) + ')' : ''));
    });
    return lines;
  }

  function pricingLines(pricing) {
    var lines = [];
    if (!pricing) return lines;
    if (pricing.base !== null && pricing.base !== undefined) lines.push('Base: ' + formatMoney(pricing.base));
    if (pricing.addOnsTotal !== null && pricing.addOnsTotal !== undefined) lines.push('Options: ' + formatMoney(pricing.addOnsTotal));
    if (pricing.requiredAddonsTotal) lines.push('Required addons: ' + formatMoney(pricing.requiredAddonsTotal));
    if (pricing.installFeeWaived) lines.push('Install fee: Waived');
    else if (pricing.installFee !== null && pricing.installFee !== undefined) lines.push('Install fee: ' + formatMoney(pricing.installFee));
    if (pricing.shipping !== null && pricing.shipping !== undefined) lines.push('Shipping: ' + formatMoney(pricing.shipping));
    if (pricing.total !== null && pricing.total !== undefined) lines.push('Build total: ' + formatMoney(pricing.total));
    return lines;
  }

  function renderBuildSummaryText(build) {
    if (!build) return '';
    var optionsByKey = build.optionsByKey || {};
    var addOnsByKey = build.addOnsByKey || {};
    var sections = [];

    var header = [];
    var vehicleLabel = formatVehicleLabel(build.vehicle ? (build.vehicle.label || build.vehicle.value || '') : '');
    if (vehicleLabel) header.push('Vehicle: ' + vehicleLabel);
    if (build.generation) header.push('Generation: ' + build.generation);
    var installLocationLabel = formatInstallLocationLabel(build.installLocation);
    if (installLocationLabel) header.push('Install location: ' + installLocationLabel);
    if (header.length) sections.push(header.join('\n'));

    var options = buildOptionsPricedLines(optionsByKey);
    if (options.length) sections.push('CONFIGURATION\n' + options.join('\n'));

    var selected = buildAddOnLines(addOnsByKey, true);
    if (selected.length) sections.push('SELECTED ADD-ONS\n' + selected.join('\n'));

    var declined = buildAddOnLines(addOnsByKey, false);
    if (declined.length) sections.push('NOT SELECTED\n' + declined.join('\n'));

    var pricing = pricingLines(build.pricing);
    if (pricing.length) sections.push('PRICING\n' + pricing.join('\n'));

    return sections.join('\n\n');
  }

  function buildEventFields(build, buildUrl) {
    var optionsByKey = (build && build.optionsByKey) || {};
    var addOnsByKey = (build && build.addOnsByKey) || {};
    var pricing = (build && build.pricing) || {};
    var optionsLines = buildOptionsLines(optionsByKey);
    var optionsPricedLines = buildOptionsPricedLines(optionsByKey);
    var selectedAddOnLines = buildAddOnLines(addOnsByKey, true);
    var declinedAddOnLines = buildAddOnLines(addOnsByKey, false);
    var vehicleLabel = formatVehicleLabel(
      build && build.vehicle ? (build.vehicle.label || build.vehicle.value || '') : ''
    );
    var installLocation = build && build.installLocation;
    var installLocationLabel = formatInstallLocationLabel(installLocation);
    var installLocationObj = installLocation && typeof installLocation === 'object' ? installLocation : {};
    var hasPricing = pricing.total !== null && pricing.total !== undefined;

    return {
      build_url: buildUrl || '',
      vehicle_label: vehicleLabel,
      generation: (build && build.generation) || '',
      install_location: installLocationLabel,
      install_location_handle: installLocationObj.handle || '',
      install_location_tax_rate: installLocationObj.salesTaxRate || '',
      install_location_shipping: installLocationObj.shippingLabel || '',
      has_install_location: installLocationLabel ? 'yes' : '',
      product_title: (build && build.product && build.product.title) || '',
      variant_title: (build && build.variant && build.variant.title) || '',
      variant_sku: (build && build.variant && build.variant.sku) || '',
      pricing_base: pricing.base !== null && pricing.base !== undefined ? String(pricing.base) : '',
      pricing_addons_total: pricing.addOnsTotal !== null && pricing.addOnsTotal !== undefined ? String(pricing.addOnsTotal) : '',
      pricing_required_addons_total: pricing.requiredAddonsTotal !== null && pricing.requiredAddonsTotal !== undefined ? String(pricing.requiredAddonsTotal) : '',
      pricing_install_fee: pricing.installFee !== null && pricing.installFee !== undefined ? String(pricing.installFee) : '',
      pricing_shipping: pricing.shipping !== null && pricing.shipping !== undefined ? String(pricing.shipping) : '',
      pricing_total: hasPricing ? String(pricing.total) : '',
      pricing_deposit: hasPricing ? String(CONFIGURATOR_DEPOSIT) : '',
      install_fee_waived: pricing.installFeeWaived ? 'yes' : '',
      has_pricing: hasPricing ? 'yes' : '',
      has_generation: (build && build.generation) ? 'yes' : '',
      has_options: optionsLines.length ? 'yes' : '',
      has_addons_selected: selectedAddOnLines.length ? 'yes' : '',
      has_addons_declined: declinedAddOnLines.length ? 'yes' : '',
      options_count: String(optionsLines.length),
      options_lines: optionsLines.join('\n'),
      options_priced_lines: optionsPricedLines.join('\n'),
      addons_selected_lines: selectedAddOnLines.join('\n'),
      addons_declined_lines: declinedAddOnLines.join('\n'),
      addons_selected_count: String(selectedAddOnLines.length),
      build_summary_html: renderBuildSummaryHtml(build),
      build_summary_text: renderBuildSummaryText(build),
    };
  }

  window.ConfiguratorBuildEmailSummary = {
    buildEventFields: buildEventFields,
    renderBuildSummaryHtml: renderBuildSummaryHtml,
    renderBuildSummaryText: renderBuildSummaryText,
    formatVehicleLabel: formatVehicleLabel,
    formatInstallLocationLabel: formatInstallLocationLabel,
  };
})();